import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createFormStore, pathKey, toPath } from '../internal/formStore'

export const FormContext = createContext(null)
/** Set by Form.List so nested Form.Item names resolve under the list path. */
export const FieldPrefixContext = createContext([])

export const useFormContext = () => useContext(FormContext)

/**
 * antd `Form.useForm()`. Returns `[form]`, where `form` is the imperative
 * instance also handed to `onFinish` consumers.
 */
export const useForm = (existing) => {
  const ref = useRef(null)
  if (!ref.current) ref.current = existing ?? createFormStore()
  return [ref.current]
}

export const useFormInstance = () => {
  const context = useFormContext()
  return context?.form ?? null
}

/**
 * Subscribes a component to one field path, re-rendering only when that path
 * (or a full reset) changes. `watchAll` opts into every change, which is what
 * `shouldUpdate` items need.
 */
export const useFieldState = (name, watchAll = false) => {
  const context = useFormContext()
  const prefix = useContext(FieldPrefixContext)
  const fullName = name == null ? null : [...prefix, ...toPath(name)]
  const key = fullName ? pathKey(fullName) : null
  const [, bump] = useState(0)

  useEffect(() => {
    if (!context) return undefined
    return context.form.subscribe((changedPaths) => {
      if (changedPaths === null || watchAll || !key) {
        bump((tick) => tick + 1)
        return
      }
      // A parent path change (list reorder, setFieldsValue on a branch) counts too.
      const affected = changedPaths.some(
        (changed) => changed === key || key.startsWith(`${changed}.`) || changed.startsWith(`${key}.`)
      )
      if (affected) bump((tick) => tick + 1)
    })
  }, [context, key, watchAll])

  return useMemo(() => ({ context, fullName, key }), [context, fullName, key])
}

/** antd `Form.useWatch(name, form)`. */
export const useWatch = (name, formOrOptions) => {
  const context = useFormContext()
  const form = formOrOptions?.getFieldValue ? formOrOptions : context?.form
  const [value, setValue] = useState(() => form?.getFieldValue(name))

  useEffect(() => {
    if (!form) return undefined
    setValue(form.getFieldValue(name))
    return form.subscribe(() => setValue(form.getFieldValue(name)))
  }, [form, pathKey(name)])

  return value
}
